import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  CalendarDays,
  Clock,
  Eye,
  EyeOff,
  Save,
  Megaphone,
  KeyRound,
  RefreshCw,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { API_BASE, formatEventDate, LOGO } from "@/content";
import { useSettings } from "@/hooks/useSettings";

const TOKEN_KEY = "shibuya_admin_token";

const emptyForm = {
  title: "",
  tag: "Trade Day",
  date: "",
  time_text: "",
  description: "",
  active: true,
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const { refresh: refreshPublicSettings } = useSettings();

  // Events
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Settings
  const [hours, setHours] = useState([]);
  const [announcement, setAnnouncement] = useState({ text: "", link: "", active: false });
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Password
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEvents([]);
  }, []);

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        toast.error("Session abgelaufen – bitte neu einloggen");
        logout();
        return true;
      }
      return false;
    },
    [logout]
  );

  const loadEvents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/events`, authHeaders());
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Events konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders, handleAuthError]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/settings`);
      setHours(res.data?.hours || []);
      setAnnouncement(res.data?.announcement || { text: "", link: "", active: false });
    } catch {
      /* defaults bleiben */
    }
  }, []);

  useEffect(() => {
    loadEvents();
    if (token) loadSettings();
  }, [loadEvents, loadSettings, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      toast.success("Eingeloggt");
    } catch (err) {
      setLoginError(
        err.response?.status === 401
          ? "Falsches Passwort"
          : "Login fehlgeschlagen – bitte erneut versuchen"
      );
    } finally {
      setLoggingIn(false);
    }
  };

  /* ---------- Events ---------- */
  const handleSyncPlatin = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/events/sync-platin`, {}, authHeaders());
      const d = res.data || {};
      toast.success(
        `Eventplaner synchronisiert: ${d.created || 0} neu, ${d.updated || 0} aktualisiert, ${d.deleted || 0} entfernt`
      );
      loadEvents();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Eventplaner konnte nicht erreicht werden");
    } finally {
      setSyncing(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) {
      toast.error("Titel und Datum sind Pflichtfelder");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/admin/events`, form, authHeaders());
      toast.success("Event erstellt");
      setForm(emptyForm);
      loadEvents();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Event konnte nicht erstellt werden");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editEvent) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE}/admin/events/${editEvent.id}`,
        {
          title: editEvent.title,
          tag: editEvent.tag,
          date: editEvent.date,
          time_text: editEvent.time_text,
          description: editEvent.description,
          active: editEvent.active,
        },
        authHeaders()
      );
      toast.success("Event aktualisiert");
      setEditEvent(null);
      loadEvents();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Event konnte nicht aktualisiert werden");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId((curr) => (curr === id ? null : curr)), 3000);
      return;
    }
    try {
      await axios.delete(`${API_BASE}/admin/events/${id}`, authHeaders());
      toast.success("Event gelöscht");
      setDeleteConfirmId(null);
      loadEvents();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Event konnte nicht gelöscht werden");
    }
  };

  const toggleActive = async (event) => {
    try {
      await axios.put(
        `${API_BASE}/admin/events/${event.id}`,
        { active: !event.active },
        authHeaders()
      );
      loadEvents();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Status konnte nicht geändert werden");
    }
  };

  /* ---------- Settings ---------- */
  const saveHours = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(`${API_BASE}/admin/settings`, { hours }, authHeaders());
      toast.success("Öffnungszeiten gespeichert");
      refreshPublicSettings();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Speichern fehlgeschlagen");
    } finally {
      setSettingsSaving(false);
    }
  };

  const saveAnnouncement = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(`${API_BASE}/admin/settings`, { announcement }, authHeaders());
      toast.success("Ankündigung gespeichert");
      refreshPublicSettings();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Speichern fehlgeschlagen");
    } finally {
      setSettingsSaving(false);
    }
  };

  /* ---------- Passwort ---------- */
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 8) {
      toast.error("Neues Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Passwort-Bestätigung stimmt nicht überein");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${API_BASE}/admin/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        authHeaders()
      );
      toast.success("Passwort geändert");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      if (!handleAuthError(err)) {
        toast.error(err.response?.data?.detail || "Passwort konnte nicht geändert werden");
      }
    } finally {
      setPwSaving(false);
    }
  };

  /* ---------- Login screen ---------- */
  if (!token) {
    return (
      <div
        data-testid="admin-login-page"
        className="hero-glow flex min-h-[80vh] items-center justify-center px-4"
      >
        <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8">
          <img src={LOGO} alt="Shibuya Store" className="mx-auto h-12 w-auto" />
          <div className="mt-6 flex items-center justify-center gap-2">
            <Lock className="h-4 w-4 text-primary" />
            <p className="font-display text-2xl uppercase tracking-wide">Admin-Bereich</p>
          </div>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Events, Öffnungszeiten &amp; mehr verwalten
          </p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="admin-password" className="text-xs uppercase tracking-wider text-muted-foreground">
                Passwort
              </Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11 rounded-xl border-border bg-background"
                autoFocus
              />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm text-destructive">
                {loginError}
              </p>
            )}
            <Button
              type="submit"
              disabled={loggingIn || !password}
              data-testid="admin-login-button"
              className="h-11 w-full rounded-xl bg-primary font-medium text-primary-foreground"
            >
              {loggingIn ? "Wird geprüft…" : "Einloggen"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  /* ---------- Dashboard ---------- */
  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
            Admin-Bereich
          </p>
          <h1 className="mt-2 font-display text-4xl uppercase tracking-[0.04em]">
            Store-Verwaltung
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={logout}
          data-testid="admin-logout-button"
          className="rounded-xl border-border bg-transparent"
        >
          <LogOut className="mr-2 h-4 w-4" /> Abmelden
        </Button>
      </div>

      <Tabs defaultValue="events" className="mt-8">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-xl bg-card p-1 sm:grid-cols-4">
          <TabsTrigger value="events" data-testid="admin-tab-events" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CalendarDays className="mr-1.5 h-4 w-4" /> Events
          </TabsTrigger>
          <TabsTrigger value="hours" data-testid="admin-tab-hours" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Clock className="mr-1.5 h-4 w-4" /> Öffnungszeiten
          </TabsTrigger>
          <TabsTrigger value="announcement" data-testid="admin-tab-announcement" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Megaphone className="mr-1.5 h-4 w-4" /> Ankündigung
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="admin-tab-security" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <KeyRound className="mr-1.5 h-4 w-4" /> Sicherheit
          </TabsTrigger>
        </TabsList>

        {/* ========== TAB: EVENTS ========== */}
        <TabsContent value="events" className="mt-6">
          <form
            onSubmit={handleCreate}
            data-testid="admin-create-form"
            className="rounded-2xl border border-border bg-card p-6"
          >
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              <p className="font-display text-2xl uppercase tracking-wide">Neues Event</p>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Titel *</Label>
                <Input
                  data-testid="admin-event-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="z. B. Shibuya Trade Day"
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kategorie</Label>
                <Input
                  data-testid="admin-event-tag-input"
                  value={form.tag}
                  onChange={(e) => setForm({ ...form, tag: e.target.value })}
                  placeholder="z. B. Trade Day, Pokémon TCG…"
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Datum *</Label>
                <Input
                  type="date"
                  data-testid="admin-event-date-input"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Uhrzeit (Text)</Label>
                <Input
                  data-testid="admin-event-time-input"
                  value={form.time_text}
                  onChange={(e) => setForm({ ...form, time_text: e.target.value })}
                  placeholder="z. B. 14:00–18:00 Uhr"
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Beschreibung</Label>
                <Textarea
                  data-testid="admin-event-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Worum geht's bei dem Event?"
                  className="mt-1.5 min-h-[90px] rounded-xl border-border bg-background"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="admin-event-active-switch"
                />
                <span className="text-sm text-muted-foreground">
                  Sofort sichtbar auf der Website
                </span>
              </div>
              <Button
                type="submit"
                disabled={saving}
                data-testid="admin-event-create-button"
                className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground"
              >
                {saving ? "Speichert…" : "Event anlegen"}
              </Button>
            </div>
          </form>

          <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-display text-2xl uppercase tracking-wide">
                Alle Events <span className="text-muted-foreground">({events.length})</span>
              </p>
              <Button
                variant="outline"
                onClick={handleSyncPlatin}
                disabled={syncing}
                data-testid="admin-sync-platin-button"
                className="h-10 rounded-xl border-border bg-transparent"
              >
                <RefreshCw className={`mr-1.5 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Synchronisiert…" : "Mit Eventplaner synchronisieren"}
              </Button>
            </div>
            <div data-testid="admin-events-list" className="mt-5 space-y-4">
              {loading ? (
                <p className="text-sm text-muted-foreground">Lädt…</p>
              ) : events.length === 0 ? (
                <p data-testid="admin-no-events" className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
                  Noch keine Events angelegt. Erstelle oben dein erstes Event – es
                  erscheint sofort auf der Events-Seite.
                </p>
              ) : (
                events.map((event) => (
                  <div
                    key={event.id}
                    data-testid={`admin-event-row-${event.id}`}
                    className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-5"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="rounded-full bg-primary/15 font-mono-ui text-[10px] uppercase tracking-wider text-primary">
                          {event.tag}
                        </Badge>
                        {!event.active && (
                          <Badge variant="secondary" className="rounded-full font-mono-ui text-[10px] uppercase tracking-wider text-muted-foreground">
                            Versteckt
                          </Badge>
                        )}
                        {event.source === "platin" && (
                          <Badge
                            variant="secondary"
                            data-testid={`admin-event-platin-${event.id}`}
                            className="rounded-full font-mono-ui text-[10px] uppercase tracking-wider text-muted-foreground"
                          >
                            aus Eventplaner
                          </Badge>
                        )}
                      </div>
                      <p className="mt-2 font-medium">{event.title}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="h-3.5 w-3.5" /> {formatEventDate(event.date)}
                        </span>
                        {event.time_text && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" /> {event.time_text}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => toggleActive(event)}
                        data-testid={`admin-event-toggle-${event.id}`}
                        title={event.active ? "Verstecken" : "Sichtbar machen"}
                        className="rounded-xl border-border bg-transparent"
                      >
                        {event.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setEditEvent({ ...event })}
                        data-testid={`admin-event-edit-${event.id}`}
                        className="rounded-xl border-border bg-transparent"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={deleteConfirmId === event.id ? "destructive" : "outline"}
                        onClick={() => handleDelete(event.id)}
                        data-testid={`admin-event-delete-${event.id}`}
                        className={`rounded-xl ${
                          deleteConfirmId === event.id ? "" : "border-border bg-transparent"
                        }`}
                      >
                        <Trash2 className="h-4 w-4" />
                        {deleteConfirmId === event.id && <span className="ml-1.5 text-xs">Wirklich?</span>}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* ========== TAB: ÖFFNUNGSZEITEN ========== */}
        <TabsContent value="hours" className="mt-6">
          <div data-testid="admin-hours-form" className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <p className="font-display text-2xl uppercase tracking-wide">Öffnungszeiten</p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Wird auf Startseite, Kontakt-Seite und im Footer angezeigt.
            </p>
            <div className="mt-5 space-y-3">
              {hours.map((row, i) => (
                <div key={i} className="flex flex-wrap items-center gap-3">
                  <Input
                    data-testid={`admin-hours-days-${i}`}
                    value={row.days}
                    onChange={(e) => {
                      const next = [...hours];
                      next[i] = { ...next[i], days: e.target.value };
                      setHours(next);
                    }}
                    placeholder="z. B. Montag – Freitag"
                    className="h-11 w-full flex-1 rounded-xl border-border bg-background sm:w-auto"
                  />
                  <Input
                    data-testid={`admin-hours-time-${i}`}
                    value={row.time}
                    onChange={(e) => {
                      const next = [...hours];
                      next[i] = { ...next[i], time: e.target.value };
                      setHours(next);
                    }}
                    placeholder="z. B. 12:00 – 20:00 Uhr"
                    className="h-11 w-full flex-1 rounded-xl border-border bg-background sm:w-auto"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setHours(hours.filter((_, idx) => idx !== i))}
                    data-testid={`admin-hours-remove-${i}`}
                    className="rounded-xl border-border bg-transparent"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => setHours([...hours, { days: "", time: "" }])}
                data-testid="admin-hours-add-button"
                className="rounded-xl border-border bg-transparent"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Zeile hinzufügen
              </Button>
              <Button
                onClick={saveHours}
                disabled={settingsSaving}
                data-testid="admin-hours-save-button"
                className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground"
              >
                <Save className="mr-1.5 h-4 w-4" /> {settingsSaving ? "Speichert…" : "Speichern"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ========== TAB: ANKÜNDIGUNG ========== */}
        <TabsContent value="announcement" className="mt-6">
          <div data-testid="admin-announcement-form" className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              <p className="font-display text-2xl uppercase tracking-wide">Ankündigungs-Banner</p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Erscheint als Leiste ganz oben auf der Website – perfekt für Trade Days,
              Aktionen oder geänderte Öffnungszeiten.
            </p>
            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Text</Label>
                <Input
                  data-testid="admin-announcement-text-input"
                  value={announcement.text}
                  onChange={(e) => setAnnouncement({ ...announcement, text: e.target.value })}
                  placeholder="z. B. Trade Day am 20.06. – 10 bis 20 Uhr im Store!"
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Link (optional)
                </Label>
                <Input
                  data-testid="admin-announcement-link-input"
                  value={announcement.link}
                  onChange={(e) => setAnnouncement({ ...announcement, link: e.target.value })}
                  placeholder="z. B. /events oder https://…"
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={announcement.active}
                  onCheckedChange={(v) => setAnnouncement({ ...announcement, active: v })}
                  data-testid="admin-announcement-active-switch"
                />
                <span className="text-sm text-muted-foreground">Banner anzeigen</span>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <Button
                onClick={saveAnnouncement}
                disabled={settingsSaving}
                data-testid="admin-announcement-save-button"
                className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground"
              >
                <Save className="mr-1.5 h-4 w-4" /> {settingsSaving ? "Speichert…" : "Speichern"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ========== TAB: SICHERHEIT ========== */}
        <TabsContent value="security" className="mt-6">
          <form
            onSubmit={handlePasswordChange}
            data-testid="admin-password-form"
            className="rounded-2xl border border-border bg-card p-6"
          >
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              <p className="font-display text-2xl uppercase tracking-wide">Passwort ändern</p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Mindestens 8 Zeichen. Nach der Änderung gilt sofort das neue Passwort.
            </p>
            <div className="mt-5 grid max-w-md grid-cols-1 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Aktuelles Passwort
                </Label>
                <Input
                  type="password"
                  data-testid="admin-pw-current-input"
                  value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Neues Passwort
                </Label>
                <Input
                  type="password"
                  data-testid="admin-pw-new-input"
                  value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Neues Passwort bestätigen
                </Label>
                <Input
                  type="password"
                  data-testid="admin-pw-confirm-input"
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
            </div>
            <div className="mt-5">
              <Button
                type="submit"
                disabled={pwSaving || !pwForm.current || !pwForm.next || !pwForm.confirm}
                data-testid="admin-pw-save-button"
                className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground"
              >
                {pwSaving ? "Speichert…" : "Passwort ändern"}
              </Button>
            </div>
          </form>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editEvent} onOpenChange={(open) => !open && setEditEvent(null)}>
        <DialogContent className="border-border bg-card sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-wide">
              Event bearbeiten
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Änderungen sind sofort auf der Website sichtbar.
            </DialogDescription>
          </DialogHeader>
          {editEvent && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Titel</Label>
                <Input
                  data-testid="admin-edit-title-input"
                  value={editEvent.title}
                  onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kategorie</Label>
                <Input
                  value={editEvent.tag}
                  onChange={(e) => setEditEvent({ ...editEvent, tag: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Datum</Label>
                <Input
                  type="date"
                  data-testid="admin-edit-date-input"
                  value={editEvent.date}
                  onChange={(e) => setEditEvent({ ...editEvent, date: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Uhrzeit (Text)</Label>
                <Input
                  value={editEvent.time_text}
                  onChange={(e) => setEditEvent({ ...editEvent, time_text: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-border bg-background"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Beschreibung</Label>
                <Textarea
                  value={editEvent.description}
                  onChange={(e) => setEditEvent({ ...editEvent, description: e.target.value })}
                  className="mt-1.5 min-h-[80px] rounded-xl border-border bg-background"
                />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <Switch
                  checked={editEvent.active}
                  onCheckedChange={(v) => setEditEvent({ ...editEvent, active: v })}
                />
                <span className="text-sm text-muted-foreground">Auf der Website sichtbar</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditEvent(null)}
              className="rounded-xl border-border bg-transparent"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={saving}
              data-testid="admin-edit-save-button"
              className="rounded-xl bg-primary font-medium text-primary-foreground"
            >
              {saving ? "Speichert…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
